// frontend/src/components/Admin/AdminDossiers.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Badge, Button, Form, Spinner, Alert, Card } from 'react-bootstrap';
import { FaEye, FaSearch, FaFilter } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const AdminDossiers = () => {
    const navigate = useNavigate();
    const [dossiers, setDossiers] = useState([]);
    const [filteredDossiers, setFilteredDossiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [visaTypeFilter, setVisaTypeFilter] = useState('all');

    const filterDossiers = useCallback(() => {
        let filtered = [...dossiers];

        if (searchTerm) {
            filtered = filtered.filter(d => 
                d.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                d.id.toString().includes(searchTerm)
            );
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter(d => d.status === statusFilter);
        }

        if (visaTypeFilter !== 'all') {
            filtered = filtered.filter(d => d.visa_type === visaTypeFilter);
        }

        setFilteredDossiers(filtered);
    }, [dossiers, searchTerm, statusFilter, visaTypeFilter]);

    useEffect(() => {
        loadDossiers();
    }, []);

    useEffect(() => {
        filterDossiers();
    }, [searchTerm, statusFilter, visaTypeFilter, dossiers, filterDossiers]);

    const loadDossiers = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/dossiers');
            const dossiersData = response.data.dossiers.data || [];

            console.log('Dossiers chargés:', dossiersData);

            const submittedDossiers = dossiersData.filter(d => d.status !== 'draft');
            setDossiers(submittedDossiers);
            setFilteredDossiers(submittedDossiers);
        } catch (err) {
            console.error('Error loading dossiers:', err);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            submitted: { variant: 'info', text: 'Soumis' },
            in_review: { variant: 'warning', text: 'En révision' },
            approved: { variant: 'success', text: 'Approuvé' },
            rejected: { variant: 'danger', text: 'Rejeté' }
        };
        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getVisaTypeLabel = (visaType) => {
        const types = {
            work: 'Travail',
            study: 'Études',
            tourist: 'Tourisme',
            family: 'Famille',
            business: 'Affaires'
        };
        return types[visaType] || visaType;
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Chargement des dossiers...</p>
            </Container>
        );
    }

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Gestion des Dossiers</h2>
                <Badge bg="primary" className="fs-6">
                    {filteredDossiers.length} dossier{filteredDossiers.length > 1 ? 's' : ''}
                </Badge>
            </div>

            <Card className="shadow-sm border-0 mb-4">
                <Card.Body>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Label><FaSearch className="me-2" />Rechercher</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Nom d'utilisateur ou ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </Form.Group>
                        </div>

                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Label><FaFilter className="me-2" />Statut</Form.Label>
                                <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="all">Tous les statuts</option>
                                    <option value="submitted">Soumis</option>
                                    <option value="in_review">En révision</option>
                                    <option value="approved">Approuvé</option>
                                    <option value="rejected">Rejeté</option>
                                </Form.Select>
                            </Form.Group>
                        </div>

                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Label><FaFilter className="me-2" />Type de Visa</Form.Label>
                                <Form.Select value={visaTypeFilter} onChange={(e) => setVisaTypeFilter(e.target.value)}>
                                    <option value="all">Tous les types</option>
                                    <option value="work">Travail</option>
                                    <option value="study">Études</option>
                                    <option value="tourist">Tourisme</option>
                                    <option value="family">Famille</option>
                                    <option value="business">Affaires</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card className="shadow-sm border-0">
                <Card.Body>
                    {filteredDossiers.length === 0 ? (
                        <Alert variant="info" className="text-center">
                            <p className="mb-0">Aucun dossier trouvé.</p>
                        </Alert>
                    ) : (
                        <Table responsive hover>
                            <thead className="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>Utilisateur</th>
                                    <th>Type de Visa</th>
                                    <th>Statut</th>
                                    <th>Date de création</th>
                                    <th>Dernière mise à jour</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDossiers.map((dossier) => (
                                    <tr key={dossier.id}>
                                        <td><strong>#{dossier.id}</strong></td>
                                        <td>{dossier.user_name || 'N/A'}</td>
                                        <td>
                                            <Badge bg="secondary">
                                                {getVisaTypeLabel(dossier.visa_type)}
                                            </Badge>
                                        </td>
                                        <td>{getStatusBadge(dossier.status)}</td>
                                        <td>{new Date(dossier.created_at).toLocaleDateString('fr-FR')}</td>
                                        <td>{new Date(dossier.updated_at).toLocaleDateString('fr-FR')}</td>
                                        <td>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => navigate(`/admin/dossier/${dossier.id}`)}
                                            >
                                                <FaEye className="me-1" />
                                                Voir
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default AdminDossiers;